import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { login } from '../../services/authService';
import ForgotPassword from './ForgotPassword';
import '../../styles/forms/login.css';

const EMAIL_ERROR_MESSAGE = 'Please enter a valid email address';
const PASSWORD_ERROR_MESSAGE = 'Password is required';

const LogIn = ({ onLogin, onShowSignUp }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const emailInputRef = useRef(null);
  const passwordInputRef = useRef(null);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(prevState => !prevState);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Validate email as user types
    if (name === 'email' && value && !validateEmail(value)) {
      setErrors(prev => ({
        ...prev,
        email: EMAIL_ERROR_MESSAGE
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
      emailInputRef.current?.focus();
    } else if (!validateEmail(formData.email)) {
      newErrors.email = EMAIL_ERROR_MESSAGE;
      emailInputRef.current?.focus();
    }
    
    if (!formData.password) {
      newErrors.password = PASSWORD_ERROR_MESSAGE;
      if (!newErrors.email) {
        passwordInputRef.current?.focus();
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    
    try {
      // 실제 로그인 API 호출
      const response = await login(formData.email, formData.password);
      
      if (response && response.success) {
        await onLogin(formData.email, formData.password);
      } else {
        setErrors({
          auth: '로그인에 실패했습니다. 이메일과 비밀번호를 확인해주세요.'
        });
      }
    } catch (error) {
      console.error('로그인 실패:', error);
      
      // 서비스에서 제공하는 사용자 친화적 메시지 사용
      setErrors({
        auth: error.userMessage || '로그인에 실패했습니다. 이메일과 비밀번호를 확인해주세요.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    setShowForgotPassword(true);
  };

  const handleBackToLogin = () => {
    setShowForgotPassword(false);
  };

  if (showForgotPassword) {
    return <ForgotPassword onBack={handleBackToLogin} />;
  }

  return (
    <div className="login-wrapper">
      <div className="login-form">
        {errors.auth && (
          <div className="error-message auth-error">{errors.auth}</div>
        )}
        <form onSubmit={handleSubmit} noValidate autoComplete="on">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              ref={emailInputRef}
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={errors.email ? 'error-input' : ''}
              placeholder="example@email.com"
              autoComplete="username"
              required
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-container">
              <input
                ref={passwordInputRef}
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={errors.password ? 'error-input' : ''}
                autoComplete="current-password"
                required
              />
              <button 
                type="button" 
                className="password-toggle" 
                onClick={togglePasswordVisibility}
                tabIndex="-1"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>
          <button 
            type="submit" 
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? 'Logging in...' : 'Log in'}
          </button>
          <button type="button" onClick={onShowSignUp} className="signup-button">Sign up</button>
          {/* <div className="forgot-password-container">
            <a href="#" className="forgot-password-link" onClick={handleForgotPassword}>Forgot Password?</a>
          </div> */}
        </form>
      </div>
    </div>
  );
};

LogIn.propTypes = {
  onLogin: PropTypes.func.isRequired,
  onShowSignUp: PropTypes.func.isRequired
};

export default LogIn; 